import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import type { TenderCase } from "../types"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

import { useDeleteTenderCase, useLeaveTenderCase } from "../hooks/use-tender-cases"
import { useTenderPaths } from "../hooks/use-tender-paths"

type TenderCaseActionDialogsProps = {
  tenderCase: TenderCase
  leaveDialogOpen: boolean
  deleteDialogOpen: boolean
  onLeaveOpenChange: (open: boolean) => void
  onDeleteOpenChange: (open: boolean) => void
}

export function TenderCaseActionDialogs({
  tenderCase,
  leaveDialogOpen,
  deleteDialogOpen,
  onLeaveOpenChange,
  onDeleteOpenChange,
}: TenderCaseActionDialogsProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { basePath } = useTenderPaths()
  const leaveCase = useLeaveTenderCase()
  const deleteCase = useDeleteTenderCase()

  return (
    <>
      <AlertDialog open={leaveDialogOpen} onOpenChange={onLeaveOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("tender.dialogs.leaveTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("tender.dialogs.leaveDescription", { title: tenderCase.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={leaveCase.isPending}>
              {t("tender.dialogs.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={leaveCase.isPending}
              onClick={() => leaveCase.mutate(tenderCase.id)}
            >
              {t("tender.dialogs.leaveConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={onDeleteOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("tender.dialogs.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("tender.dialogs.deleteDescription", { title: tenderCase.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteCase.isPending}>
              {t("tender.dialogs.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteCase.isPending}
              onClick={() => {
                // Back to the list after a failure too: a delete that failed
                // part-way can have removed records this page still shows,
                // and the list keeps the case for another try.
                deleteCase.mutate(tenderCase.id, {
                  onSettled: () => void navigate(basePath),
                })
              }}
            >
              {t("tender.dialogs.deleteConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
